import { JSX, useMemo, useRef, useState, useEffect } from 'react'

import { BoxProps } from '@chakra-ui/react'
// eslint-disable-next-line no-duplicate-imports
import { Box, VStack, Spinner, Button, Text, EmptyState } from '@chakra-ui/react'

import { RxArchive } from 'react-icons/rx'

// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Toast from '@/components/ui/toast.tsx'

import * as container from '@/lib/container.js'
import * as detective from '@/lib/detective.js'




export enum State {
	none,

	wait,
	loading,

	empty,
	finish,

}

export type Params = Record<string, unknown>

export type Handle<T> = (params: container.PagerParams) => Promise<T[]>

export type Loader<T> = {
	data : T[]
	state: State

	retrieves(): Promise<void>

}


// eslint-disable-next-line @typescript-eslint/naming-convention
export function useLoader
<	T	>
(
	handle: Handle<T>,
	params: Params = {},

	limit = 10,

)
:	Loader<T>
{
	let p = useMemo(
		() => params,

		[Object.values(params).join(',')],

	)

	let skip = useRef(0)
	let length = useRef(0)

	let [data, update_data] = useState<T[]>([])

	let [state, update_state] = useState<State>(State.none)


	let retrieves = async (): Promise<void> =>
	{
		if (state > State.wait)
		{
			return

		}

		update_state(State.loading)

		try
		{
			let v = await handle(
				{ ...params, skip: skip.current, limit, sort: ['-created'] },

			)


			update_data(prev => [...prev, ...v])

			skip.current = skip.current + limit
			length.current = length.current + v.length

			if (length.current === 0)
			{
				update_state(State.empty)

				return

			}

			if (limit > v.length)
			{
				update_state(State.finish)

				return

			}

			update_state(State.wait)

			return

		}

		catch (e)
		{
			Toast.error(e as Error)

		}

		update_state(State.finish)

	}

	useEffect(
		() =>
		{
			skip.current = 0
			length.current = 0

			update_state(State.wait)
			update_data([])

		},

		[p],

	)

	return { data, state, retrieves }

}




type Tprops<T>
	= Omit<BoxProps, 'children'>
	& {
		data: T[]
		children(item: T, index: number, length: number): JSX.Element

	}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function Ordered
<T> (props: Tprops<T>): JSX.Element
{
	return (
		<Box as="ol" display="flex" flexDirection="column" {...props}>
			{props.data.map( (item, index) => props.children(item, index, props.data.length) )}

		</Box>

	)

}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function Item (props: BoxProps): JSX.Element
{
	return (
		<Box as="li" {...props} />

	)

}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function Loading
(props: { click(): unknown }): JSX.Element
{
	let on_click = (): void =>
	{
		props.click()

	}

	return (
		<VStack pt="4" pb="4" gap="8">
			<Spinner size="sm" color="blue.200" />

			<Button size="xs" variant="outline" onClick={on_click}>加载更多</Button>
		</VStack>

	)

}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function Tail
(
	props: {
		loader(): Promise<void>

		loading   : boolean
		threshold?: number

	},

)
:	JSX.Element
{
	let threshold = props.threshold ?? 200

	let dom = useRef<null | HTMLDivElement>(null)

	useEffect(
		() =>
		{
			if (detective.is_empty(dom.current) )
			{
				return () =>
				{
					// 

				}

			}

			let observer = new IntersectionObserver(
				([e]) =>
				{
					if (e.isIntersecting === false)
					{
						return

					}

					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					props.loader()


				},

				{
					threshold: [0, 1],

					root      : dom.current?.parentElement,
					// eslint-disable-next-line @typescript-eslint/naming-convention
					rootMargin: `0px 0px ${Math.abs(threshold)}px 0px`,

				},

			)

			observer.observe(dom.current)

			return () =>
			{
				observer.disconnect()

			}

		},

		// eslint-disable-next-line @typescript-eslint/unbound-method
		[props.loader, props.loading],

	)

	return (
		<i ref={dom} style={{ display: props.loading ? 'block' : 'none', width: '100%' }} />

	)

}


// eslint-disable-next-line @typescript-eslint/naming-convention
export function Finish (): JSX.Element
{
	return (
		<Text pt="4" pb="48" fontSize="sm" color="fg.subtle" textAlign="center">没有更多数据了</Text>

	)

}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function Empty (): JSX.Element
{
	return (
		<Box h="full" pt="48" rounded="md" bg="white" shadow="sm">
			<EmptyState.Root>
				<EmptyState.Content>
					<EmptyState.Indicator>
						<RxArchive />

					</EmptyState.Indicator>

					<VStack textAlign="center">
						<EmptyState.Title>找不到数据</EmptyState.Title>

						<EmptyState.Description>您可以试着更换筛选条件或关键词重新搜索</EmptyState.Description>

					</VStack>

				</EmptyState.Content>

			</EmptyState.Root>

		</Box>

	)

}



// eslint-disable-next-line @typescript-eslint/naming-convention
export function Root
<	T	>
(
	props: Omit<Tprops<T>, 'data'>
			& {
				limit?    : number
				threshold?: number

				params?: Params
				handle : Handle<T>

			},


)
:	JSX.Element
{
	let loader = useLoader(props.handle, props.params, props.limit)

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	let on_retrieves = () => loader.retrieves()


	if (loader.state === State.none)
	{
		return (
			<></>

		)

	}

	if (loader.state === State.empty)
	{
		return (
			<Empty />

		)

	}

	if (loader.state === State.finish)
	{
		return (
			<>
				<Ordered data={loader.data} {...props} />

				<Finish />

			</>

		)

	}

	return (
		<>
			<Ordered data={loader.data} {...props} />

			<Loading click={on_retrieves} />

			<Tail loader={on_retrieves} loading={loader.state === State.wait} threshold={props.threshold} />

		</>

	)





}
